import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Wand2, Plus, ChevronRight, Bookmark } from 'lucide-react';
import type { EditorSelectionContext } from '../../../types';

export type PolishMode = 'polish' | 'condense' | 'expand';

interface EditorContextMenuProps {
  x: number;
  y: number;
  selection: EditorSelectionContext;
  onPolish: (selection: EditorSelectionContext, mode: PolishMode) => void;
  onAddToAssistant: (selection: EditorSelectionContext) => void;
  /** 把选中文字登记为伏笔/线索（弹窗里选类型 + 写备注） */
  onAddToPlotThread: (selection: EditorSelectionContext) => void;
}

const POLISH_ITEMS: Array<{ id: PolishMode; label: string; desc: string }> = [
  { id: 'polish', label: '润色', desc: '修正语病、优化表达' },
  { id: 'condense', label: '精简', desc: '压缩冗余，保留核心' },
  { id: 'expand', label: '扩写', desc: '丰富细节与描写' },
];

/**
 * 编辑器右键菜单：
 * - 润色：hover 右侧展开 润色/精简/扩写
 * - 添加为伏笔：把选中文字登记到伏笔表（弹窗里选伏笔/支线/悬念/承诺 + 备注），
 *   埋点章节取当前章、位置取选中的文档区间，之后可从伏笔页跳回这句话
 * - 添加到助手：把选中文字的位置（章节 + 段落范围）加入 AI 助手引用列表，
 *   由 AI 自行查询定位（类似 IDE agent 添加文件到上下文）
 */
export function EditorContextMenu({
  x, y, selection, onPolish, onAddToAssistant, onAddToPlotThread,
}: EditorContextMenuProps) {
  const [subOpen, setSubOpen] = useState(false);

  return createPortal(
    <div
      className="context-menu editor-context-menu"
      style={{ left: x, top: y }}
      onMouseLeave={() => setSubOpen(false)}
    >
      <div
        className="context-menu-item context-menu-has-sub"
        onMouseEnter={() => setSubOpen(true)}
      >
        <button type="button" className="context-menu-main">
          <Wand2 size={14} />
          润色
          <ChevronRight size={12} className="context-menu-chevron" />
        </button>
        {subOpen && (
          <div className="context-menu-sub">
            {POLISH_ITEMS.map(item => (
              <button
                key={item.id}
                type="button"
                className="context-menu-sub-item"
                onClick={() => onPolish(selection, item.id)}
              >
                <span className="context-menu-sub-label">{item.label}</span>
                <span className="context-menu-sub-desc">{item.desc}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        className="context-menu-item-btn"
        onClick={() => onAddToPlotThread(selection)}
      >
        <Bookmark size={14} />
        添加为伏笔
      </button>

      <button
        type="button"
        className="context-menu-item-btn"
        onClick={() => onAddToAssistant(selection)}
      >
        <Plus size={14} />
        添加到助手
      </button>
    </div>,
    document.body
  );
}
